import type React from 'react';

import { HeroLayout } from '@change/ds-core-ui';
import { cn } from '@change/ds-utils';

import { Translate } from '@change-corgi/core-react/i18n';

const Bone: React.FC<{ className: string }> = ({ className }) => (
	<div aria-hidden="true" className={cn('bg-fill-weaker animate-pulse rounded-md', className)} />
);

export const LocalSectionSkeleton: React.FC = () => (
	<HeroLayout
		reverseMd
		reverseSm
		variant="1/2"
		as="section"
		aria-busy="true"
		className="bg-gradient-background-light-3"
		media={
			<div className="flex h-full items-center p-4 md:p-12">
				<div className="flex w-full flex-col gap-4">
					{[0, 1, 2].map((i) => (
						<div key={i} className="flex items-center gap-2">
							<Bone className="size-4 shrink-0 rounded-full" />
							<Bone className="h-5 w-48" />
						</div>
					))}
				</div>
			</div>
		}
	>
		<div className="p-8 sm:p-6 md:p-12">
			<span role="status" className="sr-only">
				<Translate value="corgi.homepage.local_section.loading" />
			</span>
			<div className="space-y-6 md:p-6">
				<div className="space-y-4">
					<Bone className="h-8 w-3/4" />
					<div className="flex items-center gap-2">
						<Bone className="size-5 shrink-0 rounded-full" />
						<Bone className="h-5 w-48" />
					</div>
				</div>
				<Bone className="h-10 w-44 rounded-full" />
			</div>
		</div>
	</HeroLayout>
);

export const TrendingSectionSkeleton: React.FC = () => (
	<section aria-busy="true" className="flex flex-col gap-6">
		<span role="status" className="sr-only">
			<Translate value="corgi.homepage.trending_section.loading" />
		</span>
		<Bone className="h-8 w-1/2" />
		<Bone className="h-5 w-3/4" />
		<div className="flex flex-wrap items-center gap-2">
			{[0, 1, 2, 3, 4, 5].map((i) => (
				<Bone key={i} className="h-9 w-24 rounded-full" />
			))}
		</div>
		<div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
			{[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
				<div key={i} className="flex flex-col gap-3">
					<Bone className="aspect-[4/3] w-full rounded-lg" />
					<Bone className="h-5 w-full" />
					<Bone className="h-4 w-2/3" />
					<Bone className="h-4 w-1/2" />
				</div>
			))}
		</div>
	</section>
);

export const ContributeSectionSkeleton: React.FC = () => (
	<HeroLayout
		variant="1/2"
		reverseMd
		aria-busy="true"
		className="bg-background-raised"
		media={<Bone className="h-full min-h-[240px] w-full rounded-none" />}
	>
		<div className="flex flex-col items-start gap-6 p-8 md:p-12">
			<span role="status" className="sr-only">
				<Translate value="corgi.homepage.contribute_section.loading" />
			</span>
			<div className="w-full space-y-3">
				<Bone className="h-8 w-3/4" />
				<Bone className="h-8 w-1/2" />
			</div>
			<div className="w-full space-y-2">
				<Bone className="h-4 w-full" />
				<Bone className="h-4 w-5/6" />
			</div>
			<Bone className="h-10 w-36 rounded-full" />
		</div>
	</HeroLayout>
);

export const HomepageSkeleton: React.FC = () => (
	<div aria-busy="true">
		<span role="status" className="sr-only">
			<Translate value="corgi.homepage.loading" />
		</span>
		<section className="relative min-h-[320px] overflow-hidden md:min-h-[480px]">
			<div className="container pt-6 md:pt-[44px] lg:pt-[60px] lg:pb-11">
				<div className="mt-3 space-y-6">
					<Bone className="h-12 w-2/3 md:w-1/2" />
					<Bone className="h-6 w-3/4 md:w-2/5" />
					<Bone className="h-10 w-44 rounded-full" />
				</div>
			</div>
		</section>

		<div className="bg-background-raised">
			<div className="container space-y-8 py-16">
				<Bone className="mx-auto h-8 w-1/3" />
				<div className="grid gap-6 md:grid-cols-3">
					{[0, 1, 2].map((i) => (
						<div key={i} className="flex flex-col items-center gap-4">
							<Bone className="size-16 rounded-full" />
							<Bone className="h-5 w-3/4" />
							<Bone className="h-4 w-full" />
						</div>
					))}
				</div>
			</div>
		</div>

		<div className="container space-y-16 pt-16 pb-6 md:space-y-28 md:pt-28 md:pb-[93px]">
			<LocalSectionSkeleton />
			<TrendingSectionSkeleton />
			<ContributeSectionSkeleton />
		</div>
	</div>
);
